import { inject, Service } from '@angular/core';
import {Entity, EntityKey} from '../entity/entity';
import {RatingUnit} from '../model/rating-unit';
import {Observable, Subject} from 'rxjs';
import {SelectionSystemType} from '../model/selection-system-type';
import {ScheduleLineInput} from '../model/schedule-line-input';
import {UnitSelectionCandidate} from '../model/unit-selection-candidate';
import {SelectionModelSizeCharacteristic} from '../model/selection-model-size-characteristic';
import {RatingsModel} from '../model/ratings-model';
import {MotorType} from '../model/motor-type';
import {UnitSelectionService} from './unit-selection-service';
import {ScheduleService} from './schedule.service';
import {TagInformation} from '../model/tag-information';
import {EntityLine} from '../entity/entity-line';
import {EntityLineHeader} from '../entity/entity-line-header';

@Service()
export class RatingScheduleLineService {
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly scheduleService = inject(ScheduleService);


  addScheduleLine(schedule: Entity, unit: RatingUnit, tagInformation: TagInformation[] = [], quantity: number = 1): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    this.unitSelectionService.getProductSystemTypes(unit.productId).subscribe({
      next: (systemTypes: SelectionSystemType[]) => {
        const input = new ScheduleLineInput();
        input.scheduleId = schedule.id.entityId;
        input.tagInformation = tagInformation;
        input.quantity = quantity;
        input.candidate = new UnitSelectionCandidate();
        input.candidate.ratingRequest = unit.request;
        const filteredSystemTypes = systemTypes.filter(st => st.code === unit.systemTypeCode);
        if (filteredSystemTypes.length === 1) {
          input.candidate.systemType = filteredSystemTypes[0];
          input.candidate.coilRowCode = unit.coilRowCode;
          input.candidate.ratingResponse = unit.response;
          const modelSizeCharacteristic: SelectionModelSizeCharacteristic = new SelectionModelSizeCharacteristic();
          modelSizeCharacteristic.ratingsModel = new RatingsModel();
          modelSizeCharacteristic.ratingsModel.productId = unit.productId;
          modelSizeCharacteristic.ratingsModel.modelCode = unit.modelCode;
          modelSizeCharacteristic.modelCode = unit.modelCode;
          modelSizeCharacteristic.sizeCode = unit.sizeCode;
          input.candidate.modelSizeCharacteristic = modelSizeCharacteristic;
          const motorType: MotorType = new MotorType();
          motorType.guruModelValue = unit.motorTypeCode;
          input.candidate.motorType = motorType;
          this.scheduleService.addLineToSchedule(input).subscribe({
            next: () => subject.next('Line added'),
            error: (error) => subject.error(error),
            complete: () => subject.complete()
          });
        } else {
          subject.error('Unknown System Type Code ' + unit.systemTypeCode);
        }
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  updateScheduleLine(scheduleLine: EntityLine, unit: RatingUnit, tagInformation: TagInformation[], quantity: number): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    this.unitSelectionService.getProductSystemTypes(unit.productId).subscribe({
      next: (systemTypes: SelectionSystemType[]) => {
        const input = new ScheduleLineInput();
        input.scheduleId = scheduleLine.entityKey.entityId;
        input.line = this.getEntityLineHeader(scheduleLine);
        input.tagInformation = tagInformation;
        input.quantity = quantity;
        input.candidate = new UnitSelectionCandidate();
        input.candidate.ratingRequest = unit.request;
        const filteredSystemTypes = systemTypes.filter(st => st.code === unit.systemTypeCode);
        if (filteredSystemTypes.length === 1) {
          input.candidate.systemType = filteredSystemTypes[0];
          input.candidate.coilRowCode = unit.coilRowCode;
          input.candidate.ratingResponse = unit.response;
          const modelSizeCharacteristic: SelectionModelSizeCharacteristic = new SelectionModelSizeCharacteristic();
          modelSizeCharacteristic.ratingsModel = new RatingsModel();
          modelSizeCharacteristic.ratingsModel.productId = unit.productId;
          modelSizeCharacteristic.ratingsModel.modelCode = unit.modelCode;
          modelSizeCharacteristic.modelCode = unit.modelCode;
          modelSizeCharacteristic.sizeCode = unit.sizeCode;
          input.candidate.modelSizeCharacteristic = modelSizeCharacteristic;
          const motorType: MotorType = new MotorType();
          motorType.guruModelValue = unit.motorTypeCode;
          input.candidate.motorType = motorType;
          this.scheduleService.updateLine(input).subscribe({
            next: () => subject.next('Line added'),
            error: (error) => subject.error(error),
            complete: () => subject.complete()
          });
        } else {
          subject.error('Unknown System Type Code ' + unit.systemTypeCode);
        }
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private getEntityLineHeader(line: EntityLine): EntityLineHeader {
    const header: EntityLineHeader = new EntityLineHeader();
    const key: EntityKey = new EntityKey();
    key.entityId = line.entityKey.entityId;
    key.entityRevision = +line.entityKey.entityRevision;
    key.entityName = line.entityKey.entityType;
    header.key = key;
    header.lineId = line.lineId;
    header.product = line.product?.productId || '';
    header.description = line.description;
    header.itemNumber = line.itemNumber;
    header.quantity = line.quantityExtended;
    header.bestLeadTime = line.smallTextUserField1;
    header.unitList = line.initialUnitPrice;
    header.multiplier = line.numericUserField2;
    header.finalUnitPrice = line.finalUnitPrice;
    header.finalExtendedPrice = line.finalExtendedPrice;
    header.customerItemNumber = line.customerItemId;
    header.tags = line.externalNotes;
    header.prn = line.internalNotes;
    header.configured = line.product?.configuration;
    header.configurationComplete = line.configurationComplete;
    return header;
  }

}
